<template>
  <div class="templates">
    <CustomTable title="Группы работ" :rowsData="this.processedRowsData" :columns="this.workSetCheckGroupsColumns"
      :loading="isFetching" :show-search="true" :custom-body="true" :sticky-header="true" :showHeaderAdd="true"
      :on-add-click="this.handleAddClick">
      <template #customBody="props">
        <q-tr>
          <CustomTableCell align="center">
            {{ props.row.work_set_check_group_id }}
          </CustomTableCell>

          <CustomTableCell align="center">
            {{ props.row.work_set_check_group_name }}
          </CustomTableCell>

          <CustomTableCell align="center">
            {{ props.row.work_set_check_group_code }}
          </CustomTableCell>

          <CustomTableCell align="center" aria-required="true">
            {{ props.row.order_num }}
          </CustomTableCell>

          <CustomTableCell align="center">
            <span v-html="props.row.work_set_check_group_short_name"></span>
          </CustomTableCell>

          <CustomTableCell align="center">
            {{ getStageDisplayName(props.row.stage_id) }}
          </CustomTableCell>

          <CustomTableCell align="center">
            {{ formatPercentage(props.row.payment_part) }}
          </CustomTableCell>

          <CustomTableCell align="center">
            {{ formatPercentage(props.row.team_payment_part) }}
          </CustomTableCell>

          <CustomTableCell align="center">
            {{ getCompanyDisplayName(props.row.company_id) }}
          </CustomTableCell>

          <CustomTableCell align="center">
            <Clickable @click="() => this.handleUpdateClick(props.row)">
              <CustomIcon icon="edit" />
            </Clickable>
          </CustomTableCell>

          <CustomTableCell align="center">
            <CustomIcon icon="trash" :approve-mode="true" :loading="this.loading" :approve="() => this.onDeleteSubmit(props.row.work_set_check_group_id)
              " />
          </CustomTableCell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import BlockItem from "@/components/common/BlockItem";
import CustomDropdown from "@/components/common/CustomDropdown";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { appActions } from "@/services/store/modules/app_module/app_actions";
import CustomIcon from "@/components/common/CustomIcon";
import Clickable from "@/components/common/Clickable";

import {
  workSetCheckGroupsColumns,
  getWorkSetCheckGroups,
  addWorkSetCheckGroup,
  updateWorkSetCheckGroup,
  deleteWorkSetCheckGroup,
} from "@/views/WorkSetCheckGroups/services";

import { workSetCheckGroupsAPI } from "@/views/WorkSetCheckGroups/api";
import CheckOrClose from "@/components/common/CheckOrClose";
import { MODAL_NAMES } from "@/constants";

export default {
  name: "WorkSetCheckGroups",
  components: {
    CheckOrClose,
    Clickable,
    CustomIcon,
    CustomTableCell,
    CustomTable,
    CustomDropdown,
    BlockItem,
  },
  data() {
    return {
      workSetCheckGroupsColumns,
      rowsData: [],
      isFetching: false,
      loading: false,
      stageOptions: [],
      companyOptions: [],
      snackbar: null,
    };
  },
  created() {
    this.snackbar = useCustomSnackbar();
  },
  computed: {
    processedRowsData() {
      if (
        this.rowsData &&
        typeof this.rowsData === "object" &&
        this.rowsData.data
      ) {
        return Array.isArray(this.rowsData.data) ? this.rowsData.data : [];
      }
      return Array.isArray(this.rowsData) ? this.rowsData : [];
    },
  },
  methods: {
    getStageDisplayName(stageId) {
      const stage = this.stageOptions.find((s) => s.stage_id === stageId);
      return stage ? stage.stage_name : stageId;
    },

    getCompanyDisplayName(companyId) {
      const company = this.companyOptions.find(
        (c) => c.company_id === companyId
      );
      return company ? company.company_name || company.name : companyId;
    },

    formatPercentage(value) {
      return value !== null && value !== undefined ? `${value}%` : "";
    },

    showSucceedSnackbar(message) {
      if (this.snackbar) {
        this.snackbar.showSucceedSnackbar(message);
      }
    },

    async fetchReferenceData() {
      const [companiesResponse, stagesResponse] = await Promise.all([
        workSetCheckGroupsAPI.getCompanies(),
        workSetCheckGroupsAPI.getStages(),
      ]);

      if (companiesResponse?.data?.length) {
        this.companyOptions = companiesResponse.data;
      }

      if (stagesResponse?.data?.length) {
        this.stageOptions = stagesResponse.data;
      }
    },

    async getData() {
      this.isFetching = true;
      try {
        const res = await getWorkSetCheckGroups();
        this.rowsData = res;
      } catch (e) {
        throw e;
      } finally {
        this.isFetching = false;
      }
    },

    async handleAddClick() {
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.ADD_WORK_SET_CHECK_GROUPS, {
          onSubmit: this.onAddSubmit,
          title: "Добавить рабочую группу",
        })
      );
    },

    async handleUpdateClick(row) {
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.ADD_WORK_SET_CHECK_GROUPS, {
          onSubmit: (body) =>
            this.onUpdateSubmit(row.work_set_check_group_id, body),
          title: "Изменить рабочую группу",
          body: row,
        })
      );
    },

    async onAddSubmit(body) {
      try {
        const res = await addWorkSetCheckGroup(body);
        if (res) {
          await this.getData();
          this.showSucceedSnackbar("Рабочая группа добавлена");
          this.$store.commit(appActions.closeModal());
        }
      } catch (error) {
        this.$q.notify({
          type: "negative",
          message: error.message || "Ошибка при добавлении рабочей группы",
        });
        console.error("Error adding work set check group:", error);
        throw error;
      }
    },

    async onDeleteSubmit(work_set_check_group_id) {
      this.loading = true;
      const res = await deleteWorkSetCheckGroup(work_set_check_group_id);
      try {
        if (res) {
          await this.getData();
          this.showSucceedSnackbar("Рабочая группа удалена");
          this.$store.commit(appActions.closeModal());
        }
      } catch (error) {
        this.$q.notify({
          type: "negative",
          message: error.message || "Ошибка при удалении рабочей группы",
        });
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async onUpdateSubmit(id, body) {
      try {
        const res = await updateWorkSetCheckGroup(id, body);
        if (res) {
          await this.getData();
          this.showSucceedSnackbar("Рабочая группа обновлена");
          this.$store.commit(appActions.closeModal());
        }
      } catch (error) {
        this.$q.notify({
          type: "negative",
          message: error.message || "Ошибка при обновлении рабочей группы",
        });
        throw error;
      }
    },

    triggerError() {
      throw new Error("🔥 Искусственная ошибка в методе компонента");
    }
  },

  mounted() {
    this.fetchReferenceData();
    this.getData();
  },
};
</script>
